import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useCookies } from 'react-cookie';

// 1️⃣ Enum of cookie names
export enum AppCookieName {
  AUTH = 'auth',
  PROFILE = 'profile',
  SESSION = 'session',
}

type CookieStatus<T = any> = {
  exists: boolean;
  expired: boolean;
  available: boolean;
} & T;

// 2️⃣ Friendly interface with dot-notation
export interface AppCookies {
  auth: CookieStatus<string>;
  profile: CookieStatus<any>;
  session: CookieStatus<any>;
}

const AppCookiesContext = createContext<AppCookies | undefined>(undefined);

/**
 * Simple evaluator: just checks if cookie exists.
 */
function evaluateCookie(name: AppCookieName, cookies: Record<string, any>): CookieStatus {
  const value = cookies[name];
  if (!value) {
    return { exists: false, expired: true, available: false };
  }
  return {
    exists: true,
    expired: false,
    available: true,
    ...(typeof value === 'object' ? value : { value }),
  };
}

export const AppCookiesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [cookies] = useCookies([AppCookieName.AUTH, AppCookieName.PROFILE, AppCookieName.SESSION]);
  const [cookieState, setCookieState] = useState<AppCookies>(() => ({
    auth: evaluateCookie(AppCookieName.AUTH, cookies),
    profile: evaluateCookie(AppCookieName.PROFILE, cookies),
    session: evaluateCookie(AppCookieName.SESSION, cookies),
  }));

  // Update when cookies change
  useEffect(() => {
    setCookieState({
      auth: evaluateCookie(AppCookieName.AUTH, cookies),
      profile: evaluateCookie(AppCookieName.PROFILE, cookies),
      session: evaluateCookie(AppCookieName.SESSION, cookies),
    });
  }, [cookies]);

  const value = useMemo(() => cookieState, [cookieState]);

  return <AppCookiesContext.Provider value={value}>{children}</AppCookiesContext.Provider>;
};

// 4️⃣ Hook for use in components
export function useAppCookies() {
  const ctx = useContext(AppCookiesContext);
  if (!ctx) throw new Error('useAppCookies must be used within <AppCookiesProvider>');
  return ctx;
}
